/**
 * 채팅 React Query 캐시 조작 헬퍼 — 웹소켓 수신·REST 응답이 캐시에 반영되는 유일한 경로.
 *
 * - ["chat-messages", roomId] 는 useInfiniteQuery 의 { pages, pageParams } 구조다. 평면 배열로 다루면
 *   런타임에 터지므로(t.map is not a function → 흰 화면) 반드시 이 헬퍼를 거친다.
 * - pages[0] 이 최신 페이지이고 페이지 안은 최신순(desc) 이다. 새 메시지는 pages[0] 맨 앞에 넣는다.
 * - ["chat-rooms"] 목록의 마지막 메시지·안읽음도 여기서 갱신한다 (방 목록 컴포넌트 2곳이 각자 길이 차이로
 *   계산하던 로직은 폐기 — 엉뚱한 방에 안읽음이 붙던 원인).
 *
 * 데스크톱 앱(desktop/src/renderer/src/lib/messagesCache.ts)과 같은 모델이다.
 */
import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import type { Message } from "@/types/chat";
import type { ChatRoomWithDetails } from "@/services/chatService";

export const CHAT_ROOMS_KEY = ["chat-rooms"] as const;
export const chatMessagesKey = (roomId: number) => ["chat-messages", roomId] as const;

export type ChatMessagePages<T = Message> = InfiniteData<T[], number | undefined>;

/** 기존 캐시 메시지 변형 (삭제 표시·반응 갱신 등). 미변경 시 참조를 유지해 불필요한 리렌더를 막는다. */
export function updateCachedChatMessages<T>(
  queryClient: QueryClient,
  roomId: number,
  update: (message: T) => T
) {
  queryClient.setQueryData<ChatMessagePages<T>>(chatMessagesKey(roomId), (old) => {
    if (!old?.pages) return old;
    let changed = false;
    const pages = old.pages.map((page) => {
      let pageChanged = false;
      const next = page.map((message) => {
        const updated = update(message);
        if (updated !== message) pageChanged = true;
        return updated;
      });
      if (!pageChanged) return page;
      changed = true;
      return next;
    });
    return changed ? { ...old, pages } : old;
  });
}

export type UpsertResult = "inserted" | "updated" | "no-cache";

/**
 * 새 메시지를 캐시에 넣는다. 이미 있으면 필드를 병합(서버 확정본 우선), 캐시가 없는 방(열어본 적 없음)이면
 * 아무것도 하지 않는다 — 그 방은 열 때 서버에서 받는다.
 */
export function upsertCachedChatMessage<T extends Message>(queryClient: QueryClient, message: T): UpsertResult {
  let result: UpsertResult = "no-cache";
  queryClient.setQueryData<ChatMessagePages<T>>(chatMessagesKey(message.room_id), (old) => {
    if (!old?.pages) return old;
    const exists = old.pages.some((page) => page.some((m) => m.id === message.id));
    if (exists) {
      result = "updated";
      return {
        ...old,
        pages: old.pages.map((page) => page.map((m) => (m.id === message.id ? { ...m, ...message } : m))),
      };
    }
    result = "inserted";
    const [first = [], ...rest] = old.pages;
    return { ...old, pages: [[message, ...first], ...rest] };
  });
  return result;
}

export function getCachedChatMessage<T extends Message = Message>(
  queryClient: QueryClient,
  roomId: number,
  messageId: number
): T | undefined {
  const data = queryClient.getQueryData<ChatMessagePages<T>>(chatMessagesKey(roomId));
  for (const page of data?.pages ?? []) {
    const found = page.find((m) => m.id === messageId);
    if (found) return found;
  }
  return undefined;
}

export function updateCachedChatRoom(
  queryClient: QueryClient,
  roomId: number,
  updater: (room: ChatRoomWithDetails) => ChatRoomWithDetails
) {
  queryClient.setQueryData<ChatRoomWithDetails[]>(CHAT_ROOMS_KEY, (old) =>
    old ? old.map((r) => (r.id === roomId ? updater(r) : r)) : old
  );
}

function lastActivity(room: ChatRoomWithDetails): number {
  const t = room.last_message?.created_at ?? room.created_at;
  if (!t) return 0;
  // 서버는 KST 오프셋이 붙은 ISO 문자열을 주지만, 오프셋이 없는 값도 KST 로 해석한다
  return new Date(/(Z|[+-]\d{2}:?\d{2})$/.test(t) ? t : `${t}+09:00`).getTime() || 0;
}

/**
 * 수신 메시지를 방 목록에 반영: 마지막 메시지(시스템 메시지는 제외), 안읽음 +1(옵션), 최근 활동순 재정렬.
 * 방이 목록에 없으면 false (호출자가 목록을 다시 받는다 — 새로 초대된 방).
 */
export function applyIncomingMessageToRooms(
  queryClient: QueryClient,
  message: Message & { user_name?: string | null; user?: { id: string; name: string } | null },
  options: { countUnread: boolean }
): boolean {
  let found = false;
  queryClient.setQueryData<ChatRoomWithDetails[]>(CHAT_ROOMS_KEY, (old) => {
    if (!old) return old;
    const isSystem = message.message_type === "system";
    const next = old.map((room) => {
      if (room.id !== message.room_id) return room;
      found = true;
      const lastMessage = isSystem
        ? room.last_message
        : ({
            ...(room.last_message ?? {}),
            id: message.id,
            content: message.content,
            message_type: message.message_type,
            user_id: message.user_id,
            created_at: message.created_at,
            user: message.user ?? (message.user_name ? { name: message.user_name } : room.last_message?.user),
          } as ChatRoomWithDetails["last_message"]);
      return {
        ...room,
        last_message: lastMessage,
        unread_count:
          options.countUnread && !isSystem ? (room.unread_count ?? 0) + 1 : room.unread_count,
      };
    });
    if (!found) return old;
    return [...next].sort((a, b) => lastActivity(b) - lastActivity(a));
  });
  return found;
}

/** 내가 방을 읽었다 — 안읽음 0 + 내 멤버십의 읽음 위치 갱신 (다른 창에서 읽은 경우에도 호출) */
export function resetCachedRoomUnread(
  queryClient: QueryClient,
  roomId: number,
  meId?: string,
  messageId?: number
) {
  const now = new Date().toISOString();
  updateCachedChatRoom(queryClient, roomId, (room) => ({
    ...room,
    unread_count: 0,
    members: meId
      ? (room.members ?? []).map((m) =>
          m.user_id === meId
            ? {
                ...m,
                last_read_at: now,
                last_read_message_id: Math.max(messageId ?? 0, m.last_read_message_id ?? 0) || m.last_read_message_id,
              }
            : m
        )
      : room.members,
  }));
}

/** 다른 멤버의 읽음 위치 갱신 ('read' 프레임) */
export function setCachedMemberRead(queryClient: QueryClient, roomId: number, userId: string, messageId: number) {
  const now = new Date().toISOString();
  updateCachedChatRoom(queryClient, roomId, (room) => ({
    ...room,
    members: (room.members ?? []).map((m) =>
      m.user_id === userId
        ? { ...m, last_read_at: now, last_read_message_id: Math.max(messageId, m.last_read_message_id ?? 0) }
        : m
    ),
  }));
}
